import React, {createContext, useContext, useReducer, useEffect} from 'react';
import {AppContext} from './state';
import {reducer, SUPPORTED_EDITOR_ROLES} from './openapi-context-reducer';

const OpenApiContext = createContext({});

// Role is sourced from AppContext (which itself prefers the portal-supplied
// `<TenetEditor user={...}>` prop over the legacy `window.userRole` shim).
// The library page's URL-driven role override (`?role=...`) still flows
// through this context's `change-role` dispatch — see pages/library.js. Past
// incident (gap #10 from portal Day 11): this context used to read
// `window.userRole` directly and ignored `propUser.role`, so portal-mounted
// users always landed as Subscriber and edit affordances were silently off.
const initialState = {
    role: undefined,
    specValidationErrors: [],
};

const OpenApiContextProvider = React.memo(function ({children, backend}) {
    const appContext = useContext(AppContext);
    const appRole = appContext && appContext.state ? appContext.state.role : undefined;

    const [state, dispatch] = useReducer(
        (state, action) => reducer(state, action, {backend}),
        initialState,
    );

    useEffect(() => {
        // Mirror AppContext.state.role into OpenApiContext.state.role. Falls
        // back to the window/env shim only when AppContext hasn't resolved
        // a role yet (truly anonymous standalone dev with no propUser, no
        // window.userRole, no VITE_ROLE).
        const resolved = appRole || (typeof window !== 'undefined' && window.userRole) || import.meta.env.VITE_ROLE || 'Subscriber';
        if (resolved !== state.role) {
            dispatch({type: 'change-role', role: resolved});
        }
    }, [appRole]);

    const value = {state, dispatch, SUPPORTED_EDITOR_ROLES, backend};
    return <OpenApiContext.Provider value={value}>{children}</OpenApiContext.Provider>;
});

const OpenApiContextConsumer = OpenApiContext.Consumer;

export {OpenApiContext, OpenApiContextProvider, OpenApiContextConsumer};
